/**
 * 画像ファイル（JPEG/PNG）の基本検証に関するユーティリティ関数
 */

const SOF_BASELINE = 0xC0;
const SOF_EXTENDED = 0xC1;
const SOF_PROGRESSIVE = 0xC2;
const SOF_LOSSLESS = 0xC3;

export const JPEG_FORMAT = Object.freeze({
  BASELINE: 'baseline',
  EXTENDED: 'extended',
  PROGRESSIVE: 'progressive',
  LOSSLESS: 'lossless',
});

const SOF_MARKER_TO_FORMAT = {
  [SOF_BASELINE]: JPEG_FORMAT.BASELINE,
  [SOF_EXTENDED]: JPEG_FORMAT.EXTENDED,
  [SOF_PROGRESSIVE]: JPEG_FORMAT.PROGRESSIVE,
  [SOF_LOSSLESS]: JPEG_FORMAT.LOSSLESS,
};

export const VALIDATION_REASON = Object.freeze({
  INVALID_FILE_TYPE: 'invalid_file_type',
  IMAGE_SIZE_TOO_LARGE: 'image_size_too_large',
  JPEG_LOSSLESS_UNSUPPORTED: 'jpeg_lossless_unsupported',
});

export const VALIDATION_WARNING = Object.freeze({
  JPEG_PROGRESSIVE: 'jpeg_progressive',
});

/**
 * ファイルのMIMEタイプがJPEGかどうかをチェック
 * @param {File} file - 検証するファイル
 * @returns {boolean} JPEGのMIMEタイプの場合true
 */
export function isValidJpegMimeType(file) {
  return file.type.match(/image\/jpe?g/) !== null;
}

/**
 * ファイルの拡張子がJPEGかどうかをチェック
 * @param {File} file - 検証するファイル
 * @returns {boolean} JPEG拡張子の場合true
 */
export function isValidJpegExtension(file) {
  const fileName = file.name.toLowerCase();
  return fileName.match(/\.(jpg|jpeg)$/) !== null;
}

/**
 * ファイルのシグネチャ（マジックナンバー）をチェック
 * @param {File} file - 検証するファイル
 * @returns {Promise<boolean>} JPEGシグネチャの場合true
 */
export function checkJpegSignature(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arrayBuffer = e.target.result;
      const bytes = new Uint8Array(arrayBuffer, 0, 4);
      // JPEGファイルの先頭バイト: FF D8 FF
      if (bytes.length >= 3 &&
          bytes[0] === 0xFF &&
          bytes[1] === 0xD8 &&
          bytes[2] === 0xFF) {
        resolve(true);
      } else {
        resolve(false);
      }
    };
    reader.onerror = () => resolve(false);
    reader.readAsArrayBuffer(file.slice(0, 4));
  });
}

/**
 * @typedef {Object} JpegDimensions
 * @property {number} width
 * @property {number} height
 * @property {'baseline'|'extended'|'progressive'|'lossless'} format
 */

/**
 * JPEG画像の寸法（幅・高さ）と形式を取得
 * @param {File} file - 解析するJPEGファイル
 * @returns {Promise<JpegDimensions|null>} 画像の寸法と形式、またはnull
 */
export function getJpegDimensions(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arrayBuffer = e.target.result;
      const dimensions = parseJpegDimensions(arrayBuffer);
      resolve(dimensions);
    };
    reader.onerror = () => resolve(null);
    // JPEGヘッダー解析のため最初の64KBを読み込む（通常は十分）
    reader.readAsArrayBuffer(file.slice(0, 65536));
  });
}

/**
 * JPEG ArrayBufferから画像の寸法と形式を解析
 * @param {ArrayBuffer} arrayBuffer - JPEG画像のArrayBuffer
 * @returns {JpegDimensions|null} 画像の寸法と形式、またはnull
 */
function parseJpegDimensions(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer);
  let offset = 2; // FF D8をスキップ

  while (offset < bytes.length - 1) {
    // セグメントマーカーを探す（FF XX）
    if (bytes[offset] !== 0xFF) {
      offset += 1;
    } else {
      const marker = bytes[offset + 1];
      offset += 2;

      // SOF0〜SOF3 セグメントを探す（baseline / extended / progressive / lossless）
      if (marker >= SOF_BASELINE && marker <= SOF_LOSSLESS) {
        if (offset + 5 < bytes.length) {
          // セグメント長をスキップ（2バイト）、精度をスキップ（1バイト）
          // eslint-disable-next-line no-bitwise
          const height = (bytes[offset + 3] << 8) + bytes[offset + 4];
          // eslint-disable-next-line no-bitwise
          const width = (bytes[offset + 5] << 8) + bytes[offset + 6];
          return { width, height, format: SOF_MARKER_TO_FORMAT[marker] };
        }
        break;
      }

      // セグメント長を読み取り、次のセグメントへスキップ
      if (offset + 1 < bytes.length) {
        // eslint-disable-next-line no-bitwise
        const segmentLength = (bytes[offset] << 8) + bytes[offset + 1];
        offset += segmentLength;
      } else {
        break;
      }
    }
  }

  return null; // 寸法が見つからない場合
}

/**
 * ファイルのMIMEタイプがPNGかどうかをチェック
 * @param {File} file - 検証するファイル
 * @returns {boolean} PNGのMIMEタイプの場合true
 */
export function isValidPngMimeType(file) {
  return file.type === 'image/png';
}

/**
 * ファイルの拡張子がPNGかどうかをチェック
 * @param {File} file - 検証するファイル
 * @returns {boolean} PNG拡張子の場合true
 */
export function isValidPngExtension(file) {
  const fileName = file.name.toLowerCase();
  return fileName.match(/\.png$/) !== null;
}

/**
 * PNGファイルのシグネチャ（マジックナンバー）をチェック
 * @param {File} file - 検証するファイル
 * @returns {Promise<boolean>} PNGシグネチャの場合true
 */
export function checkPngSignature(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arrayBuffer = e.target.result;
      const bytes = new Uint8Array(arrayBuffer, 0, 8);
      // PNGファイルの先頭バイト: 89 50 4E 47 0D 0A 1A 0A
      if (bytes.length >= 8 &&
          bytes[0] === 0x89 &&
          bytes[1] === 0x50 &&
          bytes[2] === 0x4E &&
          bytes[3] === 0x47 &&
          bytes[4] === 0x0D &&
          bytes[5] === 0x0A &&
          bytes[6] === 0x1A &&
          bytes[7] === 0x0A) {
        resolve(true);
      } else {
        resolve(false);
      }
    };
    reader.onerror = () => resolve(false);
    reader.readAsArrayBuffer(file.slice(0, 8));
  });
}

/**
 * PNG画像の寸法（幅・高さ）を取得
 * @param {File} file - 解析するPNGファイル
 * @returns {Promise<{width: number, height: number}|null>} 画像の寸法またはnull
 */
export function getPngDimensions(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arrayBuffer = e.target.result;
      const dimensions = parsePngDimensions(arrayBuffer);
      resolve(dimensions);
    };
    reader.onerror = () => resolve(null);
    // PNGヘッダー解析のため最初の32バイトを読み込む
    reader.readAsArrayBuffer(file.slice(0, 32));
  });
}

/**
 * PNG ArrayBufferから画像の寸法を解析
 * @param {ArrayBuffer} arrayBuffer - PNG画像のArrayBuffer
 * @returns {{width: number, height: number}|null} 画像の寸法またはnull
 */
function parsePngDimensions(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer);

  // PNGシグネチャチェック（8バイト）
  if (bytes.length < 24) return null;

  // IHDRチャンクは常にPNGシグネチャの直後（8バイト目から）
  // IHDRチャンクの構造: [長さ(4)] [IHDR(4)] [幅(4)] [高さ(4)] ...
  // 幅は16バイト目から、高さは20バイト目から
  // eslint-disable-next-line no-bitwise
  const width = (bytes[16] << 24) | (bytes[17] << 16) | (bytes[18] << 8) | bytes[19];
  // eslint-disable-next-line no-bitwise
  const height = (bytes[20] << 24) | (bytes[21] << 16) | (bytes[22] << 8) | bytes[23];

  return { width, height };
}

/**
 * JPEGファイルの包括的なバリデーション
 * @param {File} file - 検証するJPEGファイル
 * @param {Object} options - バリデーションオプション
 * @param {number} options.maxWidth - 最大幅（省略可）
 * @param {number} options.maxHeight - 最大高さ（省略可）
 * @returns {Promise<{isValid: boolean, reason: string|null}>}
 */
async function validateJpegFile(file, options = {}) {
  const { maxWidth, maxHeight } = options;

  // MIMEタイプチェック
  if (!isValidJpegMimeType(file)) {
    return { isValid: false, reason: 'invalid_file_type' };
  }

  // 拡張子チェック
  if (!isValidJpegExtension(file)) {
    return { isValid: false, reason: 'invalid_file_type' };
  }

  // ファイルシグネチャチェック
  const hasValidSignature = await checkJpegSignature(file);
  if (!hasValidSignature) {
    return { isValid: false, reason: 'invalid_file_type' };
  }

  // 画像サイズチェック
  if (maxWidth || maxHeight) {
    const dimensions = await getJpegDimensions(file);
    if (dimensions) {
      if ((maxWidth && dimensions.width > maxWidth) ||
          (maxHeight && dimensions.height > maxHeight)) {
        return { isValid: false, reason: 'image_size_too_large' };
      }
    }
  }

  return { isValid: true, reason: null };
}

/**
 * PNGファイルの包括的なバリデーション
 * @param {File} file - 検証するPNGファイル
 * @param {Object} options - バリデーションオプション
 * @param {number} options.maxWidth - 最大幅（省略可）
 * @param {number} options.maxHeight - 最大高さ（省略可）
 * @returns {Promise<{isValid: boolean, reason: string|null}>}
 */
async function validatePngFile(file, options = {}) {
  const { maxWidth, maxHeight } = options;

  // MIMEタイプチェック
  if (!isValidPngMimeType(file)) {
    return { isValid: false, reason: 'invalid_file_type' };
  }

  // 拡張子チェック
  if (!isValidPngExtension(file)) {
    return { isValid: false, reason: 'invalid_file_type' };
  }

  // ファイルシグネチャチェック
  const hasValidSignature = await checkPngSignature(file);
  if (!hasValidSignature) {
    return { isValid: false, reason: 'invalid_file_type' };
  }

  // 画像サイズチェック
  if (maxWidth || maxHeight) {
    const dimensions = await getPngDimensions(file);
    if (dimensions) {
      if ((maxWidth && dimensions.width > maxWidth) ||
          (maxHeight && dimensions.height > maxHeight)) {
        return { isValid: false, reason: 'image_size_too_large' };
      }
    }
  }

  return { isValid: true, reason: null };
}

/**
 * 画像ファイル（JPEG/PNG）の包括的なバリデーション
 * @param {File} file - 検証するファイル
 * @param {Object} options - バリデーションオプション
 * @param {string[]} options.allowedTypes - 許可するファイルタイプ ['jpeg', 'png']
 * @param {number} options.maxWidth - 最大幅（省略可）
 * @param {number} options.maxHeight - 最大高さ（省略可）
 * @returns {Promise<{isValid: boolean, reason: string|null}>}
 */
export async function validateImageFile(file, options = {}) {
  const { allowedTypes = ['jpeg', 'png'], maxWidth, maxHeight } = options;

  const isJpeg = file.type.match(/image\/jpe?g/);
  const isPng = file.type === 'image/png';

  // 許可されたタイプかチェック
  const jpegAllowed = allowedTypes.includes('jpeg');
  const pngAllowed = allowedTypes.includes('png');

  if ((isJpeg && !jpegAllowed) || (isPng && !pngAllowed) || (!isJpeg && !isPng)) {
    return { isValid: false, reason: 'invalid_file_type' };
  }

  // JPEG検証
  if (isJpeg) {
    return validateJpegFile(file, { maxWidth, maxHeight });
  }

  // PNG検証
  if (isPng) {
    return validatePngFile(file, { maxWidth, maxHeight });
  }

  return { isValid: false, reason: 'invalid_file_type' };
}

/**
 * @typedef {Object} ValidationResult
 * @property {boolean} isValid
 * @property {string|null} reason  // VALIDATION_REASON の値、または null
 * @property {string} [warning]    // VALIDATION_WARNING の値
 */

/**
 * 画像のピクセル数制限と JPEG 形式をチェック
 * @param {File} file - チェック対象のファイル
 * @param {Object} options - オプション
 * @param {number} options.maxShortSide - 短辺の最大値
 * @param {number} options.maxTotalPixels - 総ピクセル数の最大値
 * @returns {Promise<ValidationResult>}
 */
export async function validateImagePixelLimits(file, { maxShortSide, maxTotalPixels }) {
  const dimensions = await getJpegDimensions(file);
  if (!dimensions) return { isValid: false, reason: VALIDATION_REASON.INVALID_FILE_TYPE };

  if (dimensions.format === JPEG_FORMAT.LOSSLESS) {
    return { isValid: false, reason: VALIDATION_REASON.JPEG_LOSSLESS_UNSUPPORTED };
  }

  const { width, height } = dimensions;
  if (maxShortSide && Math.min(width, height) > maxShortSide) {
    return { isValid: false, reason: VALIDATION_REASON.IMAGE_SIZE_TOO_LARGE };
  }
  if (maxTotalPixels && width * height > maxTotalPixels) {
    return { isValid: false, reason: VALIDATION_REASON.IMAGE_SIZE_TOO_LARGE };
  }

  if (dimensions.format === JPEG_FORMAT.PROGRESSIVE) {
    return { isValid: true, reason: null, warning: VALIDATION_WARNING.JPEG_PROGRESSIVE };
  }
  return { isValid: true, reason: null };
}
